import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';

const Cart = () => {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeItem, getTotal, getItemCount, loading } = useCart();

  const handleUpdateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    try {
      await updateQuantity(itemId, newQuantity);
    } catch (error) {
      toast.error('Failed to update quantity');
    }
  };

  const handleRemove = async (itemId) => {
    try {
      await removeItem(itemId);
      toast.success('Item removed from cart');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const subtotal = getTotal();
  const tax = subtotal * 0.20; // 20% VAT
  const total = subtotal + tax;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-gray-700 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Shopping Cart</h1>

        {cart.items.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center" data-testid="empty-cart">
            <ShoppingBag className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Your cart is empty</h2>
            <p className="text-gray-600 mb-6">Add some assays to get started</p>
            <Button onClick={() => navigate('/workflow')} data-testid="continue-shopping">
              Start Designing
            </Button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map((item, index) => (
                <div
                  key={item.id}
                  className="bg-white rounded-lg shadow-sm p-6"
                  data-testid={`cart-item-${index}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-lg capitalize">
                        {item.technology.replace(/_/g, ' ')} - {item.application.replace(/_/g, ' ')}
                      </h3>

                      {/* Detailed configuration section */}
                      <details className="mt-3 group" open>
                        <summary className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-700 select-none">
                          Details (click to toggle)
                        </summary>
                        <div className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 space-y-1.5" data-testid={`cart-item-details-${index}`}>
                          {Object.entries(item.configuration || {}).map(([key, value]) => {
                            if (value === null || value === undefined || value === '') return null;

                            // Nested array (e.g. oligos[], items[]) — render compactly
                            if (Array.isArray(value)) {
                              return (
                                <div key={key}>
                                  <span className="font-medium text-gray-900 capitalize">{key.replace(/_/g, ' ')}:</span>
                                  <ol className="list-decimal list-inside mt-1 space-y-1 ml-2">
                                    {value.map((entry, i) => (
                                      <li key={i} className="text-xs">
                                        {typeof entry === 'object' && entry !== null
                                          ? Object.entries(entry)
                                              .filter(([, v]) => v !== null && v !== undefined && v !== '' && (typeof v !== 'object' || Object.keys(v || {}).length > 0))
                                              .map(([k, v]) =>
                                                typeof v === 'object'
                                                  ? `${k}: ${JSON.stringify(v)}`
                                                  : `${k}: ${v}`
                                              )
                                              .join(' • ')
                                          : String(entry)}
                                      </li>
                                    ))}
                                  </ol>
                                </div>
                              );
                            }

                            // Nested object — flatten
                            if (typeof value === 'object') {
                              const inner = Object.entries(value)
                                .filter(([, v]) => v !== null && v !== undefined && v !== '')
                                .map(([k, v]) => `${k}: ${v}`)
                                .join(' • ');
                              if (!inner) return null;
                              return (
                                <div key={key}>
                                  <span className="font-medium text-gray-900 capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                                  <span className="break-all">{inner}</span>
                                </div>
                              );
                            }

                            return (
                              <div key={key}>
                                <span className="font-medium text-gray-900 capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                                <span className="break-all font-mono text-xs">{String(value)}</span>
                              </div>
                            );
                          })}
                        </div>
                      </details>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemove(item.id)}
                      data-testid={`remove-item-${index}`}
                    >
                      <Trash2 className="w-5 h-5 text-red-500" />
                    </Button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                        data-testid={`decrease-quantity-${index}`}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="w-12 text-center font-medium" data-testid={`quantity-${index}`}>
                        {item.quantity}
                      </span>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                        data-testid={`increase-quantity-${index}`}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Unit Price: €{item.price.toFixed(2)}</p>
                      <p className="text-xl font-bold text-gray-900" data-testid={`item-total-${index}`}>
                        €{(item.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Order Summary</h2>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal ({getItemCount()} items)</span>
                    <span data-testid="subtotal">€{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>VAT (20%)</span>
                    <span data-testid="tax">€{tax.toFixed(2)}</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between text-lg font-bold text-gray-900">
                    <span>Total</span>
                    <span data-testid="total">€{total.toFixed(2)}</span>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/checkout')}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="proceed-to-checkout"
                >
                  Proceed to Checkout
                </Button>
                <Button
                  onClick={() => navigate('/workflow')}
                  variant="outline"
                  className="w-full mt-3"
                  data-testid="continue-shopping-button"
                >
                  Continue Shopping
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Cart;
